import { gql, useMutation, useQuery } from '@apollo/client';
import Button from '@orn/shared-web/components/Button/Button';
import Form from '@orn/shared-web/components/Form';
import Input from '@orn/shared-web/components/Input';
import Modal from '@orn/shared-web/components/Modal';
import Toggle from '@orn/shared-web/components/Toggle';
import { DollarIcon } from '@orn/shared-web/icons';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { useUpdateFinancialAccountSettings } from '../../hooks/mutations/useUpdateFinancialAccountSettings';
import { getThresholdKind, type ThresholdKind } from '../../utils/accounts';
import { buildAccountSettingsInput } from '../settings/NotificationSettingsForm/accountAlerts';
import {
  formatDollars,
  formatPercent,
  validateAccountDollars,
  validateAccountPercent,
} from '../settings/NotificationSettingsForm/notificationSettingsFields';

import type {
  UpdateAccountModalDataQuery,
  UpdateAccountModalDataQueryVariables,
  UpdateFinancialAccountMutation,
  UpdateFinancialAccountMutationVariables,
} from './__generated__/UpdateAccountModal.gql';

const UPDATE_FINANCIAL_ACCOUNT = gql`
  mutation UpdateFinancialAccount($input: UpdateFinancialAccountInput!) {
    updateFinancialAccount(input: $input) {
      id
      name
    }
  }
`;

// The modal is opened with just the account id + name; it fetches the account's type and current
// per-account overrides here (rather than widening the caller's fragment), plus the household
// defaults so an empty input can hint the inherited value.
const UPDATE_ACCOUNT_MODAL_DATA = gql`
  query UpdateAccountModalData($id: ID!) {
    getFinancialAccount(id: $id) {
      id
      type
      subtype
      settings {
        lowBalanceThreshold
        creditUtilizationThreshold
        disableThreshold
      }
    }
    getUser {
      id
      family {
        id
        settings {
          lowBalanceThreshold
          creditUtilizationThreshold
        }
      }
    }
  }
`;

const MAX_NAME_LENGTH = 255;

interface IAccount {
  id: string;
  name: string;
}

// ---------------------------------------------------------------------------
// Base (presentational)
// ---------------------------------------------------------------------------

export interface IBaseUpdateAccountModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSubmit: (data: { name: string; threshold: number | null; disableThreshold: boolean }) => void;
  isLoading?: boolean;
  /** The account's type + overrides are still loading — hold the alert controls behind a placeholder. */
  isSettingsLoading?: boolean;
  /** Initial account nickname. */
  initialName: string;
  /** The threshold this account type supports, or null while data loads / for an unsupported type. */
  thresholdKind: ThresholdKind | null;
  /** The account's own override value (null = inheriting the household default). */
  initialThreshold: number | null;
  /** The account's mute state. */
  initialDisableThreshold: boolean;
  /** Household default for the account's threshold, shown as the input placeholder. */
  householdDefault: number | null;
  /** A load or save failure to surface beside the actions. */
  errorMessage?: string;
}

/**
 * Presentational account-edit modal. Owns the name / threshold / mute input
 * state, seeded from the loaded props (the threshold + mute arrive
 * asynchronously with the account's settings, so they re-seed on load while the
 * name — a stable prop — does not). The threshold controls render only for a
 * supported depository / credit account; muting disables the threshold input.
 */
export const BaseUpdateAccountModal = ({
  isOpen,
  onOpenChange,
  onSubmit,
  isLoading = false,
  isSettingsLoading = false,
  initialName,
  thresholdKind,
  initialThreshold,
  initialDisableThreshold,
  householdDefault,
  errorMessage,
}: IBaseUpdateAccountModalProps) => {
  const [name, setName] = React.useState(initialName);
  const [threshold, setThreshold] = React.useState('');
  const [disableThreshold, setDisableThreshold] = React.useState(initialDisableThreshold);

  // (Re)seed the fields each time the modal opens (and when it's reused for
  // another account). The modal can stay mounted across open/close
  // (AccountPaneHeader toggles `isOpen`), so gating on `isOpen` is what discards
  // an unsaved edit on reopen. Name is available synchronously from the prop;
  // keeping it out of the async threshold/mute effect means the settings query
  // resolving mid-edit can't overwrite a name the user is still typing.
  React.useEffect(() => {
    if (isOpen) {
      setName(initialName);
    }
  }, [isOpen, initialName]);

  // Threshold + mute arrive asynchronously with the account's settings, so they
  // re-seed when those load as well as on open. Their inputs aren't rendered
  // until the settings load, so this never clobbers a value the user is editing.
  React.useEffect(() => {
    if (isOpen) {
      setDisableThreshold(initialDisableThreshold);
      setThreshold(
        thresholdKind === 'creditUtilization' ? formatPercent(initialThreshold) : formatDollars(initialThreshold)
      );
    }
  }, [isOpen, initialThreshold, initialDisableThreshold, thresholdKind]);

  const nameError = React.useMemo(() => {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      return 'Name cannot be empty';
    }
    if (trimmed.length > MAX_NAME_LENGTH) {
      return 'Name cannot be longer than 255 characters';
    }
    return undefined;
  }, [name]);

  const thresholdError =
    thresholdKind === 'creditUtilization' ? validateAccountPercent(threshold) : validateAccountDollars(threshold);

  const isValid = !nameError && !thresholdError;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) {
      return;
    }
    const parsed = threshold.trim() === '' ? null : Number(threshold);
    onSubmit({
      name: name.trim(),
      // Muting clears the override intent; a set value is only meaningful when alerts are on.
      threshold: disableThreshold ? null : parsed,
      disableThreshold,
    });
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const placeholder =
    householdDefault != null
      ? thresholdKind === 'creditUtilization'
        ? String(householdDefault)
        : householdDefault.toFixed(2)
      : undefined;

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} className="w-100">
      <Modal.Header onClose={handleClose}>
        <div className="flex flex-col gap-0.5">
          <div className="text-primary text-2xl font-semibold">Account details</div>
          <div className="text-tertiary text-sm font-normal">
            Updating your account details below will affect how your account is displayed in the app.
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="flex-col items-start gap-5 px-6 pb-6 pt-0">
        <Form onSubmit={handleSubmit} className="w-full">
          <div className="flex w-full flex-col gap-8">
            <div className="flex w-full flex-col gap-5">
              <Input label="Name" value={name} onChange={setName} isInvalid={Boolean(nameError)} hint={nameError} />

              {isSettingsLoading && <div className="text-tertiary text-sm">Loading alert settings…</div>}

              {thresholdKind === 'lowBalance' && (
                <Input
                  type="number"
                  label="Low balance alert"
                  description="Alert when this account's balance falls below this amount. Leave empty to use the household default."
                  icon={DollarIcon}
                  placeholder={placeholder}
                  value={threshold}
                  onChange={setThreshold}
                  isInvalid={Boolean(thresholdError)}
                  hint={thresholdError}
                  isDisabled={disableThreshold}
                  size="md"
                />
              )}

              {thresholdKind === 'creditUtilization' && (
                <div className="flex flex-col gap-1.5">
                  <div className="text-secondary text-sm font-medium">Credit utilization alert</div>
                  <div className="text-tertiary text-sm">
                    Alert when this card's balance reaches this percent of its limit. Leave empty to use the household
                    default.
                  </div>
                  <div className="flex items-start gap-2">
                    <Input
                      type="number"
                      placeholder={placeholder}
                      value={threshold}
                      onChange={setThreshold}
                      isInvalid={Boolean(thresholdError)}
                      hint={thresholdError}
                      isDisabled={disableThreshold}
                      size="md"
                      className="flex-1"
                    />
                    <span className="text-tertiary text-md shrink-0 pt-2.5">%</span>
                  </div>
                </div>
              )}

              {thresholdKind && (
                <Toggle
                  label="Turn off alerts for this account"
                  size="md"
                  isSelected={disableThreshold}
                  onChange={setDisableThreshold}
                />
              )}
            </div>

            {errorMessage && <span className="text-error-primary text-sm">{errorMessage}</span>}

            <div className="flex w-full gap-3">
              <Button color="secondary" onClick={handleClose} isDisabled={isLoading} className="flex-1" size="lg">
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                isLoading={isLoading}
                isDisabled={!isValid || isLoading}
                className="flex-1"
                size="lg"
              >
                Save details
              </Button>
            </div>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Wrapper (data)
// ---------------------------------------------------------------------------

interface IUpdateAccountModalProps {
  account: IAccount;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSubmit?: () => void;
}

/**
 * Edit modal for a financial account: renames it, and — for a supported depository or credit account
 * — sets a per-account low-balance / credit-utilization alert threshold or turns the account's alert
 * off. Fetches the account's type and current overrides (plus the household defaults for the input
 * placeholder) when opened; persists the name and settings independently on save.
 */
const UpdateAccountModal = ({ account, isOpen, onOpenChange, onSubmit }: IUpdateAccountModalProps) => {
  const {
    data,
    loading: dataLoading,
    error: dataError,
  } = useQuery<UpdateAccountModalDataQuery, UpdateAccountModalDataQueryVariables>(UPDATE_ACCOUNT_MODAL_DATA, {
    variables: { id: account.id },
    skip: !isOpen,
  });

  const [updateFinancialAccount, { loading: nameSaving }] = useMutation<
    UpdateFinancialAccountMutation,
    UpdateFinancialAccountMutationVariables
  >(UPDATE_FINANCIAL_ACCOUNT);
  const [updateFinancialAccountSettings, { loading: settingsSaving }] = useUpdateFinancialAccountSettings();
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const loaded = data?.getFinancialAccount;
  const familySettings = data?.getUser?.family?.settings;

  const thresholdKind: ThresholdKind | null = loaded ? getThresholdKind(loaded) : null;

  const initialThreshold =
    thresholdKind === 'creditUtilization'
      ? (loaded?.settings.creditUtilizationThreshold ?? null)
      : (loaded?.settings.lowBalanceThreshold ?? null);
  const householdDefault =
    thresholdKind === 'creditUtilization'
      ? (familySettings?.creditUtilizationThreshold ?? null)
      : thresholdKind === 'lowBalance'
        ? (familySettings?.lowBalanceThreshold ?? null)
        : null;

  const handleSubmit = async (formData: { name: string; threshold: number | null; disableThreshold: boolean }) => {
    setSaveError(null);
    try {
      if (formData.name !== account.name) {
        await updateFinancialAccount({ variables: { input: { id: account.id, name: formData.name } } });
      }

      if (thresholdKind !== null) {
        const settingsChanged =
          formData.threshold !== initialThreshold ||
          formData.disableThreshold !== (loaded?.settings.disableThreshold ?? false);
        if (settingsChanged) {
          await updateFinancialAccountSettings({
            variables: {
              input: buildAccountSettingsInput({
                id: account.id,
                kind: thresholdKind,
                threshold: formData.threshold,
                muted: formData.disableThreshold,
              }),
            },
          });
        }
      }

      onSubmit?.();
      onOpenChange(false);
    } catch (error) {
      logger.error('Failed to update financial account:', error);
      setSaveError("Couldn't save your changes. Please try again.");
    }
  };

  const errorMessage =
    saveError ?? (dataError ? "Couldn't load this account's alert settings. Please try again." : undefined);

  return (
    <BaseUpdateAccountModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit}
      isLoading={nameSaving || settingsSaving}
      isSettingsLoading={dataLoading}
      initialName={account.name}
      thresholdKind={thresholdKind}
      initialThreshold={initialThreshold}
      initialDisableThreshold={loaded?.settings.disableThreshold ?? false}
      householdDefault={householdDefault}
      errorMessage={errorMessage}
    />
  );
};

export default UpdateAccountModal;
